import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MAX = 12;
const POLL_INTERVAL = 2000;

export const fmtDate = (d) => format(d, "yyyy-MM-dd");

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  vehicle: "",
  package_id: "",
  location: "",
  time: "",
  notes: "",
  addons: [],
  referred_by: "",
};

export function useBookingFlow() {
  const [services, setServices] = useState([]);
  const [addons, setAddons] = useState([]);
  const [deposit, setDeposit] = useState({ amount: 15, currency: "GBP" });
  const [date, setDate] = useState(undefined);
  const [availability, setAvailability] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // idle | polling | paid | failed | cancelled
  const [paymentState, setPaymentState] = useState("idle");
  const [confirmedBooking, setConfirmedBooking] = useState(null);

  const [form, setForm] = useState(EMPTY_FORM);

  // Load services
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/services`)
      .then((res) => {
        if (cancelled) return;
        setServices(res.data.services || []);
        setAddons(res.data.addons || []);
        if (res.data.deposit_amount != null) {
          setDeposit({
            amount: res.data.deposit_amount,
            currency: res.data.deposit_currency || "GBP",
          });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Load availability for the selected date
  useEffect(() => {
    if (!date) return undefined;
    let cancelled = false;
    setLoadingSlots(true);
    setForm((f) => ({ ...f, time: "" }));
    axios
      .get(`${API}/availability`, { params: { date: fmtDate(date) } })
      .then((res) => {
        if (!cancelled) setAvailability(res.data);
      })
      .catch(() => {
        if (!cancelled) setAvailability(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingSlots(false);
      });
    return () => {
      cancelled = true;
    };
  }, [date]);

  // Detect Stripe return — ?session_id=... or ?payment=cancelled (mount only)
  useEffect(() => {
    const url = new URL(window.location.href);
    const sessionId = url.searchParams.get("session_id");
    const wasCancelled = url.searchParams.get("payment") === "cancelled";
    const bookingId = url.searchParams.get("booking_id");

    if (wasCancelled && bookingId) {
      setPaymentState("cancelled");
      axios.post(`${API}/bookings/${bookingId}/cancel`).catch(() => {});
      window.history.replaceState({}, "", `${window.location.pathname}#book`);
      return;
    }

    if (sessionId) {
      setPaymentState("polling");
      window.history.replaceState({}, "", `${window.location.pathname}#book`);
      pollPaymentStatus(sessionId, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pollPaymentStatus = async (sessionId, attempt) => {
    if (attempt >= POLL_MAX) {
      setPaymentState("failed");
      return;
    }
    try {
      const res = await axios.get(`${API}/checkout/status/${sessionId}`);
      const data = res.data;
      if (data.payment_status === "paid") {
        if (data.booking_id) {
          const bRes = await axios.get(`${API}/bookings/${data.booking_id}`);
          setConfirmedBooking(bRes.data);
        }
        setPaymentState("paid");
        toast.success("Payment confirmed — your booking is secured.");
        return;
      }
      if (data.status === "expired") {
        setPaymentState("failed");
        toast.error("Payment session expired. Please try again.");
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), POLL_INTERVAL);
    } catch {
      setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), POLL_INTERVAL);
    }
  };

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const selectedPackage = useMemo(
    () => services.find((s) => s.id === form.package_id),
    [services, form.package_id]
  );

  const canSubmit = Boolean(
    form.name &&
      form.email &&
      form.phone &&
      form.vehicle &&
      form.package_id &&
      form.location &&
      form.time &&
      date
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error("Please complete every field before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, date: fmtDate(date) };
      const bookingRes = await axios.post(`${API}/bookings`, payload);
      const checkoutRes = await axios.post(`${API}/checkout/session`, {
        booking_id: bookingRes.data.id,
        origin_url: window.location.origin,
      });
      window.location.href = checkoutRes.data.url;
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Could not start checkout.");
      setSubmitting(false);
    }
  };

  const reset = () => {
    setPaymentState("idle");
    setConfirmedBooking(null);
    setForm(EMPTY_FORM);
    setDate(undefined);
  };

  return {
    services,
    addons,
    deposit,
    date,
    setDate,
    availability,
    loadingSlots,
    submitting,
    paymentState,
    setPaymentState,
    confirmedBooking,
    form,
    setForm,
    update,
    selectedPackage,
    canSubmit,
    submit,
    reset,
  };
}
